<template>
  <div class="mt-2.5">
    <!-- Style Selection Section (only for Checkbox) -->
    <div v-if="selectedElement?.dataType === 'CHECKBOX'" class="mb-6">
      <div class="mb-1.5">
        <UITextSmMedium class="text-gray-700">{{ t("common.customstyle") }}</UITextSmMedium>
      </div>
      <div class="flex gap-2.5">
        <!-- Standard Style Option -->
        <div class="flex flex-col items-center gap-1.5">
          <div
            class="style-option"
            :class="{
              'style-option-active': !selectedElement.style || selectedElement.style === 'standard',
            }"
            @click="updateStyle('standard')"
          >
            <img
              :src="StandardCheckboxIcon"
              :alt="t('common.checkboxStyles.standardAlt')"
              class="h-[16px] w-[86px] object-contain"
            />
          </div>
          <UITextSmMedium class="text-gray-600">{{ t("common.standard") }}</UITextSmMedium>
        </div>

        <!-- Bubble Style Option -->
        <div class="flex flex-col items-center gap-1.5">
          <div
            class="style-option"
            :class="{ 'style-option-active': selectedElement.style === 'bubble' }"
            @click="updateStyle('bubble')"
          >
            <img
              :src="BubbleCheckboxIcon"
              :alt="t('common.checkboxStyles.bubbleAlt')"
              class="h-[72px] w-[86px] object-contain"
            />
          </div>
          <UITextSmMedium class="text-gray-600">{{ t("common.bubble") }}</UITextSmMedium>
        </div>
      </div>
    </div>

    <div class="mb-2.5 flex items-center text-sm font-medium text-gray-700">
      <div class="ml-6 flex flex-1">
        <div v-if="!isQuiz" class="w-[47%]">{{ t("common.optionLabel") }}</div>
        <div v-if="enableCalculations">{{ t("common.calculationValue") }}</div>
      </div>
    </div>
    <VueDraggableNext
      v-model="options"
      handle=".handle"
      :class="[store.quizId ? 'space-y-8' : 'space-y-2']"
      @change="(e) => changeInOptions(e)"
    >
      <div
        v-for="(option, index) in options"
        :key="index"
        class="flex items-start justify-start space-x-2 self-stretch text-sm"
      >
        <Menu05Icon class="handle mt-2.5 h-4 w-4 cursor-move text-gray-500" />
        <div :class="productBuilderClass">
          <UIInput
            :id="`option-label-${index}`"
            v-model="options[index].label"
            class="w-full"
            :placeholder="t('common.listEditor.labelPlaceholder')"
            :status="errors[index] ? 'error' : ''"
            @focus="onInputFocus"
            @input="emit('update:options', [...options])"
          />
          <UIInputNumber
            v-if="enableCalculations"
            :id="`option-value-${index}`"
            v-model="options[index].calculatedValue"
            :show-button="false"
            size="large"
            class="w-full"
            :placeholder="t('common.listEditor.valuePlaceholder')"
            :status="errors[index] ? 'error' : ''"
            @focus="onInputFocus"
            @input="emit('update:options', [...options])"
          />
          <div v-if="isQuiz" class="flex flex-col gap-3">
            <CategoryAndScore
              :index="index"
              :selected-category="getSelectedCategoryAndScore(index)"
              @update:value="categoryAndScoreChange"
            ></CategoryAndScore>
            <Switch
              :value="jumpEnabled[index]"
              :label="t('form.conditonalLogic.jumpTo')"
              @update:value="(value) => updateJumpTo(value, index)"
            />
            <div v-if="jumpEnabled[index]" class="space-y-1">
              <UITextSmMedium>{{ t('common.question') }}</UITextSmMedium>
              <UISelect
                :id="'radio' + index"
                :value="getSelectLogicQuestion(index)"
                :options="questionOptions"
                @update:value="
                  (value) => logicOptionSelection(value, option, index, selectedElement.uuid)
                "
                @change="store.addToHistory()"
              ></UISelect>
            </div>
          </div>
        </div>

        <UIButton
          id="remove-option"
          :disabled="isDeleteDisabled(index)"
          @click="() => prepareToRemoveOption(index)"
        >
          <Trash01Icon class="h-5 w-5" />
        </UIButton>
      </div>
    </VueDraggableNext>
    <UIButton id="add-option" text class="mt-4" :disabled="isLastOptionEmpty" @click="addOption">
      <PlusIcon class="mr-1.5 h-5 w-5" />
      {{ t("common.addOption") }}
    </UIButton>
  </div>
  <DeleteConfirmationModal
    :show="showDeleteModal"
    @confirm="removeOption"
    @cancel="cancelRemoveOption"
  />
</template>

<script setup lang="ts">
import BubbleCheckboxIcon from "@/assets/img/bubble-checkbox.png";
import StandardCheckboxIcon from "@/assets/img/standard-checkbox.png";
import { useEmitter } from "@/composition/useEmitter";
import { useAppStore } from "@/store/app";
import { isCustomObjectField } from "@/util/customObjectUtils";
import { updateOptionsWithFocusPreservation } from "@/util/methods";
import { Menu05Icon, PlusIcon, Trash01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UIInput, UIInputNumber, UISelect, UITextSmMedium } from "@gohighlevel/ghl-ui";
import { isEqual } from "lodash";
import { computed, onMounted, ref, watch } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import Switch from "./../common/Elements/Switch.vue";
import CategoryAndScore from "./../quizzes/Elements/CategoryAndScore.vue";
import DeleteConfirmationModal from "./DeleteConfirmationModal.vue";
const { t } = useI18n();

const store = useAppStore();
const emitter = useEmitter();

interface QuestionOption {
  value: string;
  label: string;
  [key: string]: any; // Add index signature for compatibility with UISelect
}

const props = defineProps({
  isQuiz: {
    type: [Boolean, String],
    default: false,
  },
  options: {
    type: [Array<String>, Array<any>],
    default: () => {
      return [];
    },
  },
  questionOptions: {
    type: Array as () => any[],
    default: () => {
      return [];
    },
  },
  customFieldCreated: {
    type: Boolean,
    default: false,
  },
  selectedElement: {
    type: Object,
    default: () => {
      return {};
    },
  },
  slideIndex: {
    type: Number,
    default: 0,
  },
  elementIndex: {
    type: Number,
    default: 0,
  },
  enableCalculations: {
    type: Boolean,
    default: false,
  },
  hidePrefillValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:options", "update:style"]);
const jumpEnabled = ref({});
const isCustomObject = computed(() => isCustomObjectField((props.selectedElement as any)?.model));

// Track active input focus
const activeInputId = ref<string | null>(null);
const activeInputPosition = ref<number | null>(null);

const initializeOptions = () => {
  let optionsArray = props.options || [{ label: "", calculatedValue: "" }];

  if (Array.isArray(props.options)) {
    if (typeof props.options[0] === "string") {
      optionsArray = props.options.map((option) => ({ label: option, calculatedValue: "" }));
    } else if (typeof props.options[0] === "object" && props.options[0] !== null) {
      optionsArray = props.options.map((option) => ({
        label: option.label || "",
        calculatedValue: option.calculatedValue !== undefined ? option.calculatedValue : "",
      }));
    }
  }

  return optionsArray;
};

// eslint-disable-next-line vue/no-dupe-keys
const options = ref(initializeOptions());

const indexToRemove = ref(null);

const showDeleteModal = ref(false);

const errors = ref<string[]>([]);

const onInputFocus = (e: FocusEvent) => {
  const target = e.target as HTMLInputElement;
  if (target?.id) {
    activeInputId.value = target.id;
  }
};

const validateOptions = () => {
  errors.value = options.value?.map((option) =>
    option?.label.trim() === "" ? "The option field is required" : ""
  );
};

onMounted(() => {
  emit("update:options", [...options.value]);
});

watch(
  () => props.options,
  (newOptions) => {
    if (!isEqual(newOptions, options.value)) {
      options.value = updateOptionsWithFocusPreservation(
        newOptions,
        options.value,
        { label: "", calculatedValue: "" },
        activeInputId,
        activeInputPosition
      );
    }
  },
  { deep: true }
);

watch(
  () => options.value,
  () => {
    validateOptions();
    const activeElement = document.activeElement as HTMLElement;
    if (
      activeElement &&
      (activeElement.id?.startsWith("option-label-") ||
        activeElement.id?.startsWith("option-value-"))
    ) {
      emit("update:options", [...options.value]);
    }
  },
  { deep: true }
);

watch(
  () => props.enableCalculations,
  (newValue) => {
    if (options.value && options.value.length) {
      options.value = options.value.map((option) => {
        if (typeof option === "string") {
          return { label: option, calculatedValue: "" };
        }
        return {
          label: option.label || "",
          calculatedValue: option.calculatedValue !== undefined ? option.calculatedValue : "",
        };
      });
      emit("update:options", [...options.value]);
    }
  }
);

const productBuilderClass = computed(() => {
  if (store.quizId) {
    return "flex w-[275px] flex-1 flex-col gap-3 self-stretch";
  }
  return "flex flex-1 space-x-2";
});

const isLastOptionEmpty = computed(() => {
  const lastOption = options.value[options.value.length - 1];
  return lastOption?.label.trim() === "";
});

const addOption = () => {
  options.value.push({ label: "", calculatedValue: "" });
};

const isDeleteDisabled = (index: number): boolean => {
  if (props.hidePrefillValue) {
    return true;
  }
  if (!options.value[index]) {
    return true;
  }
  return false;
};

const updateStyle = (style: "standard" | "bubble") => {
  emit("update:style", style);
};

const prepareToRemoveOption = (index) => {
  if (options.value.length > 1) {
    if (props.customFieldCreated) {
      indexToRemove.value = index;
      showDeleteModal.value = true;
    } else {
      emitter.emit("option:deleted", {
        indexToRemove: index,
        current: props.selectedElement.uuid,
        slideIndex: props.slideIndex,
        elementIndex: props.elementIndex,
      });
      options.value.splice(index, 1);
      emit("update:options", [...options.value]);
    }
  }
};

const removeOption = async () => {
  if (indexToRemove.value !== null) {
    options.value.splice(indexToRemove.value, 1);
    emit("update:options", [...options.value]);
    emitter.emit("option:deleted", {
      indexToRemove: indexToRemove.value,
      current: props.selectedElement.uuid,
      slideIndex: props.slideIndex,
      elementIndex: props.elementIndex,
    });
  }
  showDeleteModal.value = false;
};

const cancelRemoveOption = () => {
  indexToRemove.value = null;
  showDeleteModal.value = false;
};

const getSelectLogicQuestion = (optionIndex) => {
  if (store.logic[props.selectedElement.uuid]) {
    const jumpToQuestion = store.logic[props.selectedElement.uuid][optionIndex];
    return jumpToQuestion?.jumpTo || "";
  }
  return "";
};

const setJumpToSwitch = () => {
  options.value.forEach((value, index) => {
    if (getSelectLogicQuestion(index)) {
      jumpEnabled.value[index] = true;
    }
  });
};

setJumpToSwitch();
const logicOptionSelection = (value, option, index, current) => {
  emitter.emit("logic:update", { jumpTo: value, option, index, current });
};

const categoryAndScoreChange = (category) => {
  emitter.emit("category:update", {
    ...category,
    elementIndex: props.elementIndex,
    slideIndex: props.slideIndex,
  });
};

const updateJumpTo = (value, index) => {
  jumpEnabled.value[index] = value;
};

const getSelectedCategoryAndScore = (index) => {
  if (props.selectedElement?.scoreByCategory) {
    return props.selectedElement.scoreByCategory[index] || {};
  }
  return {};
};

const changeInOptions = (e) => {
  emit("update:options", [...options.value]);
  if (e.moved) {
    emitter.emit("category:sort", {
      slideIndex: props.slideIndex,
      elementIndex: props.elementIndex,
      newIndex: e.moved.newIndex,
      oldIndex: e.moved.oldIndex,
    });
    emitter.emit("logic:sort", {
      newIndex: e.moved.newIndex,
      oldIndex: e.moved.oldIndex,
      current: props.selectedElement.uuid,
    });
  }
};
</script>

<style scoped>
.style-option {
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
  padding: 12px 36px;
  background: #ffffff;
  transition: all 0.15s ease-in-out;
  height: 96px;
}

.style-option-active {
  border: 2px solid #155eef;
  background: #eff4ff;
}
</style>
